import { Heading } from '@big-red-group/storefront-common-ui'
import {
  TopbarConfigurationCategoryType,
  TopbarConfigurationDestination,
  TopbarConfigurationDestinationState,
  TopbarConfigurationDestinationStateRegion,
  TopbarConfigurationLink,
} from '@/types'
import Icon, { IconName } from './Icons'
import {
  OffCanvasWrapper,
  OffCanvasWrapperBg,
  MenuWrapper,
  MenuGroup,
  MenuGroupHeader,
  MenuGroupHeaderLink,
  SubmenuWrapper,
  MenuGroupLabel,
  LevelOneLink,
  CloseIcon,
  SubmenuGroup,
  SubmenuGrid,
  SubmenuGridItem,
  SubmenuGridItemImage,
  ThirdMenuWrapper,
  BackLink,
  SubmenuGridItemIcon,
} from './OffCanvasMenu.styles'
import { useRouter } from 'next/router'
import { useContext } from 'react'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import Image from 'next/image'
import { IconWrapper, OffCanvasMenuItemWrapper } from '@/components/Topbar.styles'
import { useNavigationClick } from '@/hooks/HTMLElementClickHook'
import Link from 'next/link'

type OffCanvasMenuProps = {
  topbarHeight?: number
  isOpen: boolean
  submenuToggled?: string
  thirdMenuToggled?: string
  onMenuChange: (submenu: string) => void
  onThirdMenuChange: (submenu: string) => void
  onClose: () => void
  triggerLogin: () => void
  destinations?: TopbarConfigurationDestination[]
  categories?: TopbarConfigurationCategoryType[]
  displayableLinks?: TopbarConfigurationLink[]
  helpfulLinks?: TopbarConfigurationLink[]
}
const OffCanvasMenu: React.FC<OffCanvasMenuProps> = ({
  topbarHeight = 80,
  isOpen = false,
  submenuToggled,
  thirdMenuToggled,
  onMenuChange,
  onThirdMenuChange,
  onClose,
  destinations,
  categories,
  displayableLinks,
  helpfulLinks,
}) => {
  const router = useRouter()
  const defaultProperties = useContext(DefaultPropertiesContext)
  const baseUrl = getUrlPrefix(defaultProperties?.agentConfiguration?.handlerKey)
  const isBrgAgent = defaultProperties?.agentConfiguration?.isBrgAgent

  const miscMenuItems = [displayableLinks, helpfulLinks].flat()
  const otherSubmenus = Array.from(miscMenuItems.filter((item) => item?.subLinks))

  const trackNavClicked = useNavigationClick()

  const getMenuItemLink = (itemUrl: string, type: 'category' | 'destination' | 'fullUrl') => {
    switch (type) {
      // TODO: more permanent solution for VA lang param
      case 'category':
        return `${baseUrl}/${router.query?.lang ?? 'en'}/things-to-do/${itemUrl}`
      case 'destination':
        return `${baseUrl}/${router.query?.lang ?? 'en'}/${itemUrl}`
      default:
        return `${baseUrl}${itemUrl}`
    }
  }

  // TODO: once loki starts providing iconUrls, removes sublinkIcons
  const sublinkIcons: Record<string, IconName> = {
    'Gift vouchers': 'gift',
    'Redeem a voucher': 'tickets',
  }

  const handleMenuChange = (menuLabel: string) => {
    onMenuChange(menuLabel)
    onThirdMenuChange('')
  }

  const handleSubmenuLink = (label: string, url?: string) => {
    onThirdMenuChange(label)
  }

  const getFeaturedRegions = (regions: TopbarConfigurationDestinationStateRegion[]) => {
    return regions.length > 6 ? regions.slice(0, 6) : [...regions]
  }

  const getUnfeaturedRegions = (regions: TopbarConfigurationDestinationStateRegion[]) => {
    return regions.length > 6 ? regions.slice(6, regions.length) : []
  }

  const handleCloseThirdMenu = () => {
    onMenuChange('')
    onThirdMenuChange('')
  }

  const handleClickLink = (link: string) => {
    onClose()
    router.push(link)
  }

  const sortingArrays = [
    [
      'queensland',
      'new-south-wales',
      'victoria',
      'south-australia',
      'tasmania',
      'western-australia',
      'northern-territory',
      'act',
    ],
    ['north-island', 'south-island'],
  ]

  const sortStates = (states: TopbarConfigurationDestinationState[], countryIndex: number) => {
    return states.sort(
      (a, b) => sortingArrays[countryIndex].indexOf(a.url) - sortingArrays[countryIndex].indexOf(b.url)
    )
  }

  const renderDestinationSubmenu = () => {
    if (!destinations) return

    return (
      <SubmenuWrapper
        data-testid="destinations-submenu"
        className={submenuToggled === 'Destinations' ? 'submenu-active' : ''}
        thirdMenuToggled={thirdMenuToggled != ''}
      >
        <CloseIcon data-testid="submenu-close" onClick={() => onMenuChange('')} />
        {destinations &&
          destinations.map((country, index) => (
            <SubmenuGroup key={index}>
              <MenuGroupLabel>{country.name}</MenuGroupLabel>
              <SubmenuGrid className="large-thumbnail">
                {sortStates(country.states, index).map((state, stateIndex) => {
                  const unfeaturedRegions = getUnfeaturedRegions(state.regions)
                  return (
                    <SubmenuGridItem
                      data-testid={`state-${state.url}`}
                      data-stateindex={stateIndex}
                      key={state.name}
                      className="large-thumbnail"
                    >
                      <div
                        data-testid={`${state.url}-sublink`}
                        className="grid-item-wrap"
                        onClick={() => handleSubmenuLink(state.name, 'submenu')}
                      >
                        {/*
                          // todo: once loki starts providing the right image, uncomment this line.
                          {state.image && <SubmenuGridItemImage image={state.image} largeImage={true} />}
                      */}
                        {state.image && (
                          <SubmenuGridItemImage
                            src={`https://cdn.experienceoz.com.au/assets/topbar/states/${state.url}.jpg`}
                            loading="lazy"
                            largeImage
                            alt=""
                            width={186}
                            height={135}
                          />
                        )}
                        {state.name}
                      </div>

                      {state.regions && (
                        <ThirdMenuWrapper
                          data-testid={`${state.url}-third-menu`}
                          className={thirdMenuToggled === state.name ? 'submenu-active' : ''}
                        >
                          <BackLink onClick={() => onThirdMenuChange('')} data-testid={`${state.url}-back`}>
                            <Icon name="leftArrow" width={16} height={16} />
                            {state.name}
                          </BackLink>
                          <CloseIcon onClick={() => handleCloseThirdMenu()} data-testid={`${state.url}-close`} />
                          <SubmenuGroup>
                            <MenuGroupLabel>Top destinations</MenuGroupLabel>
                            <SubmenuGrid className="medium-thumbnail">
                              {getFeaturedRegions(state.regions).map((region) => (
                                <SubmenuGridItem key={region.name} className="medium-thumbnail">
                                  <Link
                                    data-testid={`${region.url}-sublink`}
                                    className="grid-item-wrap"
                                    href={getMenuItemLink(region.url, 'destination')}
                                    onClick={(event) => {
                                      trackNavClicked({
                                        menuType: `Off Canvas Submenu | ${state.name} Top Destinations`,
                                        text: region.name,
                                        url: getMenuItemLink(region.url, 'destination'),
                                        imageUrl: `https://cdn.experienceoz.com.au/assets/topbar/regions/${region.url}.jpg`,
                                        event,
                                      })
                                      onClose()
                                    }}
                                  >
                                    {/*
                                    // todo: once loki starts providing the right image, uncomment this line.
                                    {region.image && <SubmenuGridItemImage image={region.image} largeImage={false} />}
                                  */}
                                    {region.image && (
                                      <SubmenuGridItemImage
                                        src={`https://cdn.experienceoz.com.au/assets/topbar/regions/${region.url}.jpg`}
                                        loading="lazy"
                                        largeImage={false}
                                        alt=""
                                        width={80}
                                        height={80}
                                      />
                                    )}
                                    {region.name}
                                  </Link>
                                </SubmenuGridItem>
                              ))}
                            </SubmenuGrid>
                          </SubmenuGroup>
                          {unfeaturedRegions.length > 0 && (
                            <div>
                              <MenuGroupHeader>
                                <MenuGroupLabel>All destinations</MenuGroupLabel>
                                <MenuGroupHeaderLink
                                  aria-label={`View all ${state.name} experiences`}
                                  href={getMenuItemLink(state.url, 'destination')}
                                  onClick={(event) => {
                                    trackNavClicked({
                                      menuType: `Off Canvas Submenu | ${state.name} All Destinations`,
                                      text: 'View All',
                                      url: getMenuItemLink(state.url, 'destination'),
                                      event,
                                    })
                                  }}
                                >
                                  View all
                                </MenuGroupHeaderLink>
                              </MenuGroupHeader>
                              <SubmenuGrid className="medium-thumbnail">
                                {unfeaturedRegions.map((region) => (
                                  <SubmenuGridItem key={region.name} className="medium-thumbnail">
                                    <Link
                                      data-testid={`${region.url}-sublink`}
                                      className="grid-item-wrap"
                                      href={getMenuItemLink(region.url, 'destination')}
                                      onClick={(event) => {
                                        trackNavClicked({
                                          menuType: `Off Canvas Submenu | ${state.name} All Destinations`,
                                          text: region.name,
                                          url: getMenuItemLink(region.url, 'destination'),
                                          event,
                                        })
                                        onClose()
                                      }}
                                    >
                                      {region.name}
                                    </Link>
                                  </SubmenuGridItem>
                                ))}
                              </SubmenuGrid>
                            </div>
                          )}
                        </ThirdMenuWrapper>
                      )}
                    </SubmenuGridItem>
                  )
                })}
              </SubmenuGrid>
            </SubmenuGroup>
          ))}
      </SubmenuWrapper>
    )
  }

  const renderCategorySubmenu = (type: TopbarConfigurationCategoryType) => {
    const { label, categoryType, categories } = type

    return (
      <SubmenuWrapper
        data-testid={`${categoryType?.toLowerCase()}-submenu`}
        className={submenuToggled === categoryType ? 'submenu-active' : ''}
        thirdMenuToggled={thirdMenuToggled != ''}
        key={label}
      >
        <CloseIcon data-testid="submenu-close" onClick={() => onMenuChange('')} />
        <SubmenuGroup>
          <MenuGroupLabel>All {label}</MenuGroupLabel>
          <SubmenuGrid className="medium-thumbnail">
            {categories.map((category, idx) => (
              <SubmenuGridItem key={idx} className="medium-thumbnail">
                <Link
                  data-testid={`${category.url}-sublink`}
                  href={getMenuItemLink(category.url, 'category')}
                  className="grid-item-wrap"
                  onClick={(event) => {
                    trackNavClicked({
                      menuType: `Off Canvas Submenu | All ${label}`,
                      text: category.name,
                      url: getMenuItemLink(category.url, 'category'),
                      imageUrl: `https://cdn.experienceoz.com.au/assets/topbar/categories/${category.url}.jpg`,
                      event,
                    })
                    onClose()
                  }}
                >
                  {/*
                      // todo: once loki starts providing the right image, uncomment this line.
                      {category.image && <SubmenuGridItemImage image={category.image} largeImage={false} />}
                    */}
                  {category.image && (
                    <SubmenuGridItemImage
                      src={`https://cdn.experienceoz.com.au/assets/topbar/categories/${category.url}.jpg`}
                      loading="lazy"
                      largeImage={false}
                      width={80}
                      height={80}
                      alt=""
                    />
                  )}
                  {category.name}
                </Link>
              </SubmenuGridItem>
            ))}
          </SubmenuGrid>
        </SubmenuGroup>
      </SubmenuWrapper>
    )
  }

  const renderOtherSubmenus = () => {
    if (!otherSubmenus) return

    return (
      <>
        {otherSubmenus &&
          otherSubmenus.map((submenu, index) => (
            <SubmenuWrapper
              key={index}
              data-testid={`other-submenu-${index}`}
              className={submenuToggled === submenu?.label ? 'submenu-active' : ''}
              thirdMenuToggled={thirdMenuToggled != ''}
            >
              <CloseIcon data-testid="submenu-close" onClick={() => onMenuChange('')} />
              <SubmenuGroup>
                <MenuGroupLabel>{submenu?.label}</MenuGroupLabel>
                <SubmenuGrid className="medium-thumbnail">
                  {submenu?.subLinks &&
                    submenu.subLinks.map((subLink) => (
                      <SubmenuGridItem key={subLink.label} className="medium-thumbnail">
                        {subLink.url && (
                          <Link
                            data-testid={`${subLink.url}-sublink`}
                            className="grid-item-wrap"
                            href={getMenuItemLink(subLink.url, 'fullUrl')}
                            onClick={(event) => {
                              trackNavClicked({
                                menuType: `Off Canvas Submenu | ${submenu?.label}`,
                                text: subLink.label,
                                url: getMenuItemLink(subLink.url, 'fullUrl'),
                                event,
                              })
                              onClose()
                            }}
                          >
                            {/* TODO: once loki starts providing iconUrls, removes sublinkIcons */}
                            {sublinkIcons.hasOwnProperty(subLink.label) && (
                              <SubmenuGridItemIcon>
                                <Icon color="black" name={sublinkIcons[subLink.label]} width={24} height={24} />
                              </SubmenuGridItemIcon>
                            )}
                            {subLink.label}
                          </Link>
                        )}
                      </SubmenuGridItem>
                    ))}
                </SubmenuGrid>
              </SubmenuGroup>
            </SubmenuWrapper>
          ))}
      </>
    )
  }

  const renderDisplayableLink = (link: TopbarConfigurationLink, index: number) => {
    return (
      <OffCanvasMenuItemWrapper color={link.colour} data-testid={`off-canvas-link-${index}`}>
        {link.iconUrl && (
          <IconWrapper data-testid={`off-canvas-link-icon-${index}`}>
            <Image src={link.iconUrl} alt={link.label} width={16} height={16} />
          </IconWrapper>
        )}
        <Heading
          size="XS"
          as="p"
          spacing={false}
          bold={false}
          style={{ color: link.colour || 'var(--brand-secondary)' }}
        >
          {link.label}
        </Heading>
      </OffCanvasMenuItemWrapper>
    )
  }

  return (
    <OffCanvasWrapper
      topbarHeight={topbarHeight}
      data-testid="off-canvas-wrapper"
      className={isOpen ? 'menu-active' : ''}
    >
      <OffCanvasWrapperBg
        topbarHeight={topbarHeight}
        data-testid="off-canvas-wrapper-bg"
        className="menu-bg"
        onClick={onClose}
      />

      <MenuWrapper className="menu-wrapper">
        {/* EXPLORE MENU */}
        <MenuGroup>
          <MenuGroupLabel>Explore</MenuGroupLabel>
          <LevelOneLink
            data-testid="destinations"
            onClick={() => handleMenuChange('Destinations')}
            isSelected={submenuToggled === 'Destinations'}
            className={submenuToggled === 'Destinations' ? 'link-active' : ''}
          >
            <Heading size="XS" as="p" spacing={false} bold={false}>
              Destinations
            </Heading>
            <Icon name="rightarrow" width={20} height={20} />
          </LevelOneLink>
          {categories?.map((categoryType) => (
            <LevelOneLink
              key={categoryType.categoryType}
              data-testid={categoryType.categoryType?.toLowerCase()}
              onClick={() => handleMenuChange(categoryType.categoryType)}
              isSelected={submenuToggled === categoryType.categoryType}
              className={submenuToggled === categoryType.categoryType ? 'link-active' : ''}
            >
              <Heading size="XS" as="p" spacing={false} bold={false}>
                {categoryType.label}
              </Heading>
              <Icon name="rightarrow" width={20} height={20} />
            </LevelOneLink>
          ))}
          {displayableLinks &&
            displayableLinks.map((link, index) => (
              <>
                {link.url ? (
                  <LevelOneLink
                    key={link.label}
                    isSelected={submenuToggled === link.label}
                    className={submenuToggled === link.label ? 'link-active' : ''}
                  >
                    <Link
                      onClick={(event) => {
                        trackNavClicked({
                          menuType: `Off Canvas Menu | ${link.label}`,
                          text: link.label,
                          url: getMenuItemLink(link.url!, 'fullUrl'),
                          event,
                        })
                        onClose()
                      }}
                      href={getMenuItemLink(link.url!, 'fullUrl')}
                    >
                      {renderDisplayableLink(link, index)}
                    </Link>
                  </LevelOneLink>
                ) : (
                  <LevelOneLink
                    key={link.label}
                    onClick={() => {
                      handleMenuChange(link.label)
                    }}
                    isSelected={submenuToggled === link.label}
                    className={submenuToggled === link.label ? 'link-active' : ''}
                  >
                    {renderDisplayableLink(link, index)}
                    {!link.url && <Icon name="rightarrow" width={20} height={20} />}
                  </LevelOneLink>
                )}
              </>
            ))}
        </MenuGroup>
        {isBrgAgent && (
          <MenuGroup>
            <>
              <MenuGroupLabel>Inspire me</MenuGroupLabel>
              <LevelOneLink
                data-testid="Inspire me"
                isSelected={submenuToggled === 'Inspire me'}
                className={submenuToggled === 'Inspire me' ? 'link-active' : ''}
              >
                <Link
                  href={`/${router.query?.lang}/blog`}
                  onClick={(event) => {
                    trackNavClicked({
                      menuType: `Off Canvas Menu | Travel Stories`,
                      text: 'Travel Stories',
                      url: getMenuItemLink('/en/blog', 'fullUrl'),
                      event,
                    })
                    onClose()
                  }}
                >
                  <Heading size="XS" as="p" spacing={false} bold={false}>
                    Travel Stories
                  </Heading>
                </Link>
              </LevelOneLink>
            </>
          </MenuGroup>
        )}
        {/* HELPFUL LINKS MENU */}
        <MenuGroup>
          {helpfulLinks && (
            <>
              <MenuGroupLabel>Helpful links</MenuGroupLabel>
              {helpfulLinks.map((link) => (
                <>
                  {link.url ? (
                    <LevelOneLink
                      key={link.label}
                      isSelected={submenuToggled === link.label}
                      className={submenuToggled === link.label ? 'link-active' : ''}
                    >
                      <Link
                        href={getMenuItemLink(link.url!, 'fullUrl')}
                        data-testid={link.label?.toLowerCase()}
                        onClick={(event) => {
                          trackNavClicked({
                            menuType: `Off Canvas Menu | ${link.label}`,
                            text: link.label,
                            url: getMenuItemLink(link.url!, 'fullUrl'),
                            event,
                          })
                          onClose()
                        }}
                      >
                        <Heading size="XS" as="p" spacing={false} bold={false}>
                          {link.label}
                        </Heading>
                      </Link>
                    </LevelOneLink>
                  ) : (
                    <LevelOneLink
                      data-testid={link.label?.toLowerCase()}
                      key={link.label}
                      onClick={() => {
                        handleMenuChange(link.label)
                      }}
                      isSelected={submenuToggled === link.label}
                      className={submenuToggled === link.label ? 'link-active' : ''}
                    >
                      <Heading size="XS" as="p" spacing={false} bold={false}>
                        {link.label}
                      </Heading>
                    </LevelOneLink>
                  )}
                </>
              ))}
            </>
          )}
        </MenuGroup>
      </MenuWrapper>

      {renderDestinationSubmenu()}
      {categories?.map((categoryType) => renderCategorySubmenu(categoryType))}
      {renderOtherSubmenus()}
    </OffCanvasWrapper>
  )
}

export default OffCanvasMenu
